import * as XLSX from 'xlsx';
import type { RideSchema, SimulationActivity, UnmatchedRide } from '../../types/simulation';

const TARGET_BACK_ARRIVAL_ANCHORS = [
  { label: 'Day', minutes: 6 * 60 + 40 },
  { label: 'Late', minutes: 14 * 60 + 40 },
  { label: 'Night', minutes: 22 * 60 + 40 },
] as const;

const UK_POSTCODE_REGEX = /\b[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}\b/i;

type TargetBackArrivalLabel = (typeof TARGET_BACK_ARRIVAL_ANCHORS)[number]['label'];

export interface EsteLauderMtpExportInput {
  activities?: SimulationActivity[] | null;
  ridePoints?: RideSchema[] | null;
  unmatchedRides?: UnmatchedRide[] | null;
  runId?: string | null;
}

export interface EsteLauderMtpRouteRow {
  Shift: TargetBackArrivalLabel;
  'Target Back Arrival': string;
  'Staff ID': string;
  user_id: string;
  'Staff Name': string;
  Postcode: string;
  'Walk Time (min)': number | '';
  'Pickup (HH:MM)': string;
  'Arrival (HH:MM)': string;
  'Stop Assignment': string;
  'Route Assignment': string;
  'Ride ID': string;
}

export interface EsteLauderMtpIssueRow {
  'Staff ID': string;
  user_id: string;
  'Staff Name': string;
  'Ride ID': string;
  Reason: string;
  Postcode: string;
  'Requested (HH:MM)': string;
}

export interface EsteLauderMtpWorkbookData {
  routeRows: EsteLauderMtpRouteRow[];
  shiftRows: Record<TargetBackArrivalLabel, EsteLauderMtpRouteRow[]>;
  issueRows: EsteLauderMtpIssueRow[];
}

function normalizeId(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

function firstNonEmptyString(values: unknown[]): string {
  for (const value of values) {
    const next = normalizeId(value);
    if (next) return next;
  }
  return '';
}

function parseTimeToMinutes(value: unknown): number | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return null;
  const date = new Date(parsed);
  return date.getUTCHours() * 60 + date.getUTCMinutes();
}

export function formatToHm(value: unknown): string {
  if (typeof value !== 'string' || value.trim() === '') return '';
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return '';
  const date = new Date(parsed);
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

function nearestTargetBackArrival(value: unknown): { label: TargetBackArrivalLabel; text: string } {
  const minutes = parseTimeToMinutes(value);
  if (minutes == null) {
    return { label: 'Day', text: '06:40' };
  }
  let best: (typeof TARGET_BACK_ARRIVAL_ANCHORS)[number] = TARGET_BACK_ARRIVAL_ANCHORS[0];
  let smallestDistance = Number.POSITIVE_INFINITY;
  for (const candidate of TARGET_BACK_ARRIVAL_ANCHORS) {
    const direct = Math.abs(minutes - candidate.minutes);
    const wrap = 24 * 60 - direct;
    const distance = Math.min(direct, wrap);
    if (distance < smallestDistance) {
      smallestDistance = distance;
      best = candidate;
    }
  }
  const h = Math.floor(best.minutes / 60)
    .toString()
    .padStart(2, '0');
  const m = (best.minutes % 60).toString().padStart(2, '0');
  return { label: best.label, text: `${h}:${m}` };
}

function normalizeWalkMinutes(value: unknown): number | '' {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '';
  if (n < 0) return '';
  return Number(n.toFixed(2));
}

function inferPostcodeFromText(value: unknown): string {
  if (typeof value !== 'string') return '';
  const match = value.match(UK_POSTCODE_REGEX);
  return match ? match[0].toUpperCase() : '';
}

function makeRideKeyCandidates(ride: Record<string, unknown>): string[] {
  return [
    normalizeId(ride.ride_id),
    normalizeId(ride.external_reference_id),
    normalizeId(ride.request_id),
  ].filter(Boolean);
}

function indexRidePoints(ridePoints: RideSchema[] | null | undefined): Map<string, RideSchema> {
  const index = new Map<string, RideSchema>();
  for (const ride of ridePoints ?? []) {
    const keys = [
      normalizeId(ride.ride_id),
      normalizeId(ride.external_reference_id),
      normalizeId((ride as unknown as Record<string, unknown>).request_id),
    ].filter(Boolean);
    for (const key of keys) {
      if (!index.has(key)) {
        index.set(key, ride);
      }
    }
  }
  return index;
}

function toRecords(activities: SimulationActivity[] | null | undefined): Record<string, unknown>[] {
  return (activities ?? []).map((activity) => activity as Record<string, unknown>);
}

function flattenActivityRides(activity: Record<string, unknown>): Record<string, unknown>[] {
  const trips = Array.isArray(activity.trips) ? activity.trips : [];
  if (trips.length === 0) {
    return [activity];
  }
  return trips
    .map((trip) => {
      if (trip != null && typeof trip === 'object') {
        const tripRecord = trip as Record<string, unknown>;
        const rideRecord =
          tripRecord.ride && typeof tripRecord.ride === 'object'
            ? (tripRecord.ride as Record<string, unknown>)
            : {};
        return {
          ...activity,
          ...tripRecord,
          ...rideRecord,
        };
      }
      return activity;
    })
    .filter(Boolean);
}

function buildRouteRows(
  activities: SimulationActivity[] | null | undefined,
  ridePoints: RideSchema[] | null | undefined
): EsteLauderMtpRouteRow[] {
  const ridePointIndex = indexRidePoints(ridePoints);
  const rows: EsteLauderMtpRouteRow[] = [];

  for (const activity of toRecords(activities)) {
    const routeAssignment = firstNonEmptyString([
      activity.vehicle_id,
      activity.vehicle_shift_id,
      activity.shift_id,
    ]);
    const stopAssignment = firstNonEmptyString([
      activity.stop_name,
      activity.location_name,
      activity.address,
      activity.from_point_id,
      activity.to_point_id,
    ]);

    for (const rideCandidate of flattenActivityRides(activity)) {
      const keys = makeRideKeyCandidates(rideCandidate);
      const ridePoint = keys.map((key) => ridePointIndex.get(key)).find(Boolean);

      const arrivalRaw = firstNonEmptyString([
        rideCandidate.activity_end_time,
        rideCandidate.activity_start_time,
        activity.activity_end_time,
        activity.activity_start_time,
      ]);
      const pickupRaw = firstNonEmptyString([
        rideCandidate.request_time,
        ridePoint?.request_time,
        rideCandidate.activity_start_time,
      ]);

      const targetBack = nearestTargetBackArrival(arrivalRaw || pickupRaw);
      const postcode = firstNonEmptyString([
        rideCandidate.postcode,
        rideCandidate.post_code,
        rideCandidate.pickup_postcode,
        rideCandidate.from_postcode,
        ridePoint?.from_point_id,
        inferPostcodeFromText(rideCandidate.from_address),
        inferPostcodeFromText(ridePoint?.from_address),
      ]);

      rows.push({
        Shift: targetBack.label,
        'Target Back Arrival': targetBack.text,
        'Staff ID': firstNonEmptyString([
          rideCandidate.external_user_reference_id,
          rideCandidate.user_id,
          ridePoint?.external_user_reference_id,
          ridePoint?.user_id,
        ]),
        // Per-passenger Atlas user_id from the simulation source-of-truth
        // (RideSchema.user_id, populated per-passenger in round 1's expansion).
        // Falls back to the external_user_reference_id when no internal id is
        // present so the column never collapses to blank for valid riders.
        user_id: firstNonEmptyString([
          rideCandidate.user_id,
          ridePoint?.user_id,
          rideCandidate.external_user_reference_id,
          ridePoint?.external_user_reference_id,
        ]),
        'Staff Name': firstNonEmptyString([
          rideCandidate.staff_name,
          rideCandidate.user_name,
          rideCandidate.passenger_name,
          rideCandidate.name,
        ]),
        Postcode: postcode,
        'Walk Time (min)': normalizeWalkMinutes(
          firstNonEmptyString([
            rideCandidate.walk_time_minutes,
            rideCandidate.walk_minutes,
            rideCandidate.walking_time_minutes,
            rideCandidate.walking_minutes,
          ])
        ),
        'Pickup (HH:MM)': formatToHm(pickupRaw),
        'Arrival (HH:MM)': formatToHm(arrivalRaw || pickupRaw),
        'Stop Assignment': firstNonEmptyString([
          rideCandidate.to_point_id,
          rideCandidate.from_point_id,
          stopAssignment,
        ]),
        'Route Assignment': routeAssignment,
        'Ride ID': firstNonEmptyString([
          rideCandidate.ride_id,
          rideCandidate.external_reference_id,
          ridePoint?.ride_id,
          ridePoint?.external_reference_id,
        ]),
      });
    }
  }

  return rows;
}

function buildIssueRows(unmatchedRides: UnmatchedRide[] | null | undefined): EsteLauderMtpIssueRow[] {
  return (unmatchedRides ?? []).map((ride) => {
    const postcode = firstNonEmptyString([
      (ride as Record<string, unknown>).postcode,
      inferPostcodeFromText(ride.from_address),
    ]);
    return {
      'Staff ID': firstNonEmptyString([ride.external_user_reference_id, ride.user_id]),
      user_id: firstNonEmptyString([ride.user_id, ride.external_user_reference_id]),
      'Staff Name': firstNonEmptyString([
        (ride as Record<string, unknown>).staff_name,
        (ride as Record<string, unknown>).user_name,
        (ride as Record<string, unknown>).passenger_name,
      ]),
      'Ride ID': firstNonEmptyString([ride.ride_id, ride.external_reference_id, ride.request_id]),
      Reason: firstNonEmptyString([ride.reason, (ride as Record<string, unknown>).unassigned_reason]),
      Postcode: postcode,
      'Requested (HH:MM)': formatToHm(ride.request_time),
    };
  });
}

export function buildEsteLauderMtpWorkbookData(
  input: Omit<EsteLauderMtpExportInput, 'runId'>
): EsteLauderMtpWorkbookData {
  const routeRows = buildRouteRows(input.activities, input.ridePoints);
  const issueRows = buildIssueRows(input.unmatchedRides);

  const shiftRows: Record<TargetBackArrivalLabel, EsteLauderMtpRouteRow[]> = {
    Day: [],
    Late: [],
    Night: [],
  };
  for (const row of routeRows) {
    shiftRows[row.Shift].push(row);
  }

  return { routeRows, shiftRows, issueRows };
}

export function downloadEsteLauderMtpWorkbook(input: EsteLauderMtpExportInput): void {
  const { routeRows, shiftRows, issueRows } = buildEsteLauderMtpWorkbookData(input);
  if (routeRows.length === 0 && issueRows.length === 0) return;

  const workbook = XLSX.utils.book_new();
  const writeSheet = <T extends object>(name: string, rows: T[]) => {
    const payload: object[] =
      rows.length > 0
        ? rows
        : [{ Note: 'No rows available for this sheet in current simulation data.' }];
    const sheet = XLSX.utils.json_to_sheet(payload);
    XLSX.utils.book_append_sheet(workbook, sheet, name);
  };

  writeSheet('MTP Route Review', routeRows);
  writeSheet('MTP Day Shift', shiftRows.Day);
  writeSheet('MTP Late Shift', shiftRows.Late);
  writeSheet('MTP Night Shift', shiftRows.Night);
  if (issueRows.length > 0) {
    writeSheet('Transport Issues', issueRows);
  }

  const filename = input.runId ? `este_lauder_mtp_${input.runId}.xlsx` : 'este_lauder_mtp.xlsx';
  XLSX.writeFile(workbook, filename);
}
